import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckCheck, PackageCheck } from 'lucide-react';
import type { NomenclatureDto } from '@eombor/contracts';
import { Button, Card, EmptyState, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { useNomenclatures } from '../../catalog/model/catalog.queries.js';
import { accountingApi } from '../../accounting/api/accounting.api.js';
import { requisitionApi } from '../../requisition/api/requisition.api.js';
import { requisitionErrorText } from '../../requisition/model/requisition-error.js';
import {
  matchReady,
  useIssueHandover,
  useReadyHandovers,
  useUploadPhoto,
} from '../model/handover.queries.js';
import { stepBlocker, useHandoverStore } from '../model/handover.store.js';
import { useGeolocation } from '../model/useGeolocation.js';
import { useBarcodeScanner } from '../../../shared/scanner/useBarcodeScanner.js';
import { useScannerClaim } from '../../../shared/scanner/scanner-claim.js';
import { IssueStep } from './IssueStep.js';
import { PickMode } from './PickMode.js';
import { SignatureSetup } from '../../signature/ui/SignatureSetup.js';
import { ScanStep } from './ScanStep.js';

const ISSUE_PERMISSION = 'stock.issue';

/** Rasm identifikatori — brauzerda, yuklashdan oldin beriladi. */
let photoSeq = 0;
const nextPhotoId = (): string => {
  photoSeq += 1;
  return `photo-${photoSeq}`;
};

export interface HandoverPageProps {
  /** Kartochkani ochish — sahifa marshrutlashni bilmaydi. */
  onOpenRequisition?: (requisitionId: string) => void;
}

/**
 * SKLADCHINING BERISH EKRANI — skaner-birinchi.
 *
 * Oqim bosqichli va har bosqichda ekranda BITTA savol turadi: skladchi
 * qo'lqopda, bir qo'lida skaner, ikkinchisida telefon bilan ishlaydi va
 * ko'p maydonli shaklni to'ldira olmaydi.
 *
 * Serverga faqat oxirida bitta so'rov ketadi (rasmlardan tashqari — ular
 * tanlangan zahoti yuklanadi). Berish — bo'linmas hujjat va uning yarmini
 * yozib qo'yib ketib bo'lmaydi.
 */
export const HandoverPage = ({ onOpenRequisition }: HandoverPageProps) => {
  /* Bu ekran TALABNOMA raqamini kutadi — qobiqdagi umumiy skaner (katak kodi)
     jim tursin, aks holda bitta o'qish ikki joyda ishlab ketardi. */
  useScannerClaim();

  const { canAnywhere } = usePermission();
  const store = useHandoverStore();
  const [notFound, setNotFound] = useState(false);
  /** Yakunlangan berish — muvaffaqiyat ekrani do'kon tozalangandan keyin ham qoladi. */
  const [done, setDone] = useState<{ docNo: string; requisitionId: string } | null>(null);

  const ready = useReadyHandovers();
  const nomenclatures = useNomenclatures({ limit: 200 });
  const upload = useUploadPhoto();
  const issue = useIssueHandover(store.requisitionId ?? '');
  const gps = useGeolocation(store.step === 'issue');
  const reduceMotion = useReducedMotion() === true;

  /**
   * Talabnomani KARTOCHKA bilan oladi: ro'yxatda satrlar yo'q, berish uchun esa
   * har satrning tasdiqlangan miqdori kerak.
   */
  const open = useMutation({
    /*
     * Talabnoma bilan BIRGA uning trebovaniyasi ham olinadi.
     *
     * Mahsulot aynan qog'ozdagi «ТРЕБОВАНИЕ №» bo'yicha beriladi: olib
     * ketayotgan odamning qo'lida shu raqamli qog'oz turadi va skladchi
     * ikkovini solishtiradi. Talabnoma raqami esa ichki hujjat raqami va
     * u qog'ozda umuman yo'q.
     */
    mutationFn: async (requisitionId: string) => {
      const requisition = await requisitionApi.byId(requisitionId);
      const surveys = await accountingApi.listSurveys({ requisitionId, limit: 1 });
      return { requisition, formNo: surveys.items[0]?.formNo ?? null };
    },
    onSuccess: ({ requisition, formNo }) => {
      setNotFound(false);
      store.pick({
        id: requisition.id,
        docNo: requisition.docNo,
        formNo,
        /* Oluvchi TALABNOMADAN keladi: buyurtmachi kimni yuborishini
           allaqachon ko'rsatgan va skladchi uni qayta yozmaydi. */
        throughPerson: requisition.throughPerson,
        lines: requisition.lines.map((line) => ({
          lineId: line.id,
          lineNo: line.lineNo,
          nomenclatureId: line.nomenclatureId,
          requestedQty: line.requestedQty,
          // Tasdiqlangan miqdor `null` bo'lsa so'ralgani olinadi — backenddagi
          // `effectiveQtyOf` bilan bir xil qoida.
          approvedQty: line.approvedQty ?? line.requestedQty,
        })),
      });
    },
  });

  const nameOf = useMemo(
    () =>
      new Map<string, NomenclatureDto>(
        (nomenclatures.data?.items ?? []).map((item) => [item.id, item]),
      ),
    [nomenclatures.data],
  );

  const findByCode = (code: string): void => {
    const found = matchReady(ready.rows, code);
    if (found === null) {
      setNotFound(true);
      return;
    }
    open.mutate(found.id);
  };

  /*
   * Skaner butun ekran bo'ylab tinglanadi: fokus qayerda bo'lishidan
   * qat'i nazar skanerlash ishlashi kerak.
   *
   * Natija HECH QAYERDA ko'rsatilmaydi va bu ataylab: ekranda skaner
   * maydoni yo'q, skanerlangan kod esa talabnomani to'g'ridan-to'g'ri
   * ochadi. Topilmasa — ro'yxat tepasida xato chiqadi.
   */
  useBarcodeScanner((code) => {
    store.setCode(code);
    if (store.step === 'scan') findByCode(code);
  });

  const pickPhotos = (files: File[]): void => {
    for (const file of files) {
      const id = nextPhotoId();
      // Oldindan ko'rish YUKLASHNI KUTMAYDI: skladchi olgan suratini darhol
      // ko'rmasa, u ikkinchi marta suratga oladi.
      store.addPhoto({ id, name: file.name, previewUrl: URL.createObjectURL(file) });
      /*
       * Har fayl uchun ALOHIDA va'da (`mutateAsync`), `mutate` ning chaqiruv
       * ilmoqlari emas: ikkinchi `mutate` birinchisining `onSuccess` ini
       * almashtirib yuboradi va birinchi rasm abadiy "yuklanmoqda" bo'lib
       * qolardi. Bir vaqtda bir nechta rasm tanlash esa oddiy hol.
       */
      void upload
        .mutateAsync({ file, context: 'HANDOVER_PHOTO' })
        .then((stored) =>
          useHandoverStore.getState().markPhoto(id, { status: 'ready', fileId: stored.id }),
        )
        .catch(() => useHandoverStore.getState().markPhoto(id, { status: 'failed' }));
    }
  };

  const submit = (): void => {
    const state = useHandoverStore.getState();
    if (state.requisitionId === null) return;

    issue.mutate(
      {
        /* Oluvchining ismi YUBORILMAYDI: uni server talabnomaning
           «Kim orqali» sidan o'zi oladi. Mijoz yuborsa, ikki manba
           bir kun bir-biridan ajralib ketardi. */
        photoFileIds: state.photos.flatMap((photo) =>
          photo.fileId === null ? [] : [photo.fileId],
        ),
        lines: state.lines.map((line) => ({ lineId: line.lineId, issuedQty: line.issuedQty })),
        ...(state.comment.trim() === '' ? {} : { comment: state.comment.trim() }),
        ...(gps === null ? {} : { gps }),
      },
      {
        onSuccess: (result) => {
          setDone({ docNo: result.requisition.docNo, requisitionId: result.requisition.id });
          store.reset();
        },
      },
    );
  };

  if (!canAnywhere(ISSUE_PERMISSION)) {
    return (
      <Card>
        <EmptyState
          icon={<PackageCheck size={20} aria-hidden />}
          title={t('handover.forbidden')}
          description={t('handover.forbiddenHint')}
        />
      </Card>
    );
  }

  if (done !== null) {
    return (
      <motion.div
        // Muvaffaqiyat ekrani — to'liq va yashil: skladchi telefonni cho'ntagiga
        // solishdan oldin natijani bir qarashda ko'rishi kerak.
        initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          'flex min-h-[60vh] flex-col items-center justify-center gap-4 rounded-[var(--radius-lg)]',
          'border border-accent/25 bg-accent-soft p-8 text-center',
        )}
      >
        <span className="flex size-16 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-contrast">
          <CheckCheck size={30} aria-hidden />
        </span>
        <div className="flex flex-col gap-1">
          <p className="text-2xl font-semibold tracking-tight text-text">
            {t('handover.doneTitle')}
          </p>
          <p className="tabular text-sm text-text-muted">
            {t('handover.doneHint', { docNo: done.docNo })}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button size="lg" onClick={() => setDone(null)}>
            {t('handover.doneNew')}
          </Button>
          {onOpenRequisition === undefined ? null : (
            <Button
              variant="secondary"
              size="lg"
              onClick={() => onOpenRequisition(done.requisitionId)}
            >
              {t('handover.doneOpenCard')}
            </Button>
          )}
        </div>
      </motion.div>
    );
  }

  /*
   * TO'SIQ har doim ko'rinadi — bosilgandan KEYIN emas.
   *
   * Ilgari sabab faqat "Keyingisi" bosilib, o'tish rad etilgandan keyin
   * paydo bo'lardi. Bir ekranli oqimda esa tugma darhol o'chiq turadi va
   * yonida nega o'chiqligi yozilgan bo'lishi kerak.
   */
  const blocker = stepBlocker(store);
  const stepError = blocker === null ? null : t(blocker);

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight text-text">{t('handover.title')}</h1>
        <p className="max-w-prose text-sm text-text-muted">{t('handover.subtitle')}</p>
      </header>

      {/*
        BOSQICHLAR CHIZIG'I OLIB TASHLANDI.

        U beshta bosqich uchun kerak edi — "yana qancha qoldi?" degan
        savolga javob berardi. Ikkita ekran uchun esa u shovqin: ekranning
        o'zi qayerdaligini aytadi (kod qidirish yoki berish).
      */}
      {store.step === 'pick' && store.requisitionId !== null ? (
        /*
         * YIG'IB OLISH — TO'LIQ EKRAN.
         *
         * U sahifaning ichida emas, ustida turadi (`fixed inset-0`):
         * skladchi bu ekranni javon oldida, qo'lqopda ishlatadi va unga
         * yon panel ham, sarlavha ham kerak emas.
         */
        <PickMode
          requisitionId={store.requisitionId}
          docNo={store.docNo ?? ''}
          formNo={store.formNo}
          throughPerson={store.throughPerson}
          /* Orqaga — SKANERLASHGA: boshqa talabnomaga o'tish uchun
             qoralamani butunlay tashlab yuborish kerak. */
          onBack={store.reset}
          onDone={(rows) => {
            store.setIssued(rows);
            store.next();
          }}
        />
      ) : null}

      {store.step === 'scan' ? (
        <Card>
          <ScanStep
            ready={ready.rows}
            nomenclatures={nameOf}
            onPick={(id) => open.mutate(id)}
            loading={ready.isPending || open.isPending}
            error={
              notFound
                ? t('handover.scanNotFound')
                : open.isError
                  ? requisitionErrorText(open.error, t('handover.loadError'))
                  : null
            }
          />
        </Card>
      ) : store.step === 'issue' ? (
        /*
         * Imzo bo'limi `IssueStep` ning ICHIDA emas, yonida: `IssueStep`
         * sof taqdimot komponenti va ma'lumot so'ramaydi (testi uni
         * provider'siz render qiladi). So'rov yuboradigan qism shu yerda,
         * sahifada turadi.
         */
        <>
        <SignatureSetup />
        <IssueStep
            docNo={store.docNo}
            formNo={store.formNo}
            throughPerson={store.throughPerson}
            lines={store.lines}
            nomenclatures={nameOf}
            photos={store.photos}
            comment={store.comment}
            onComment={store.setComment}
            onPickPhotos={pickPhotos}
            onRemovePhoto={store.removePhoto}
            onSubmit={submit}
            /* Orqaga — YIG'IB OLISHGA: rasm olayotganda «yana bittasi
               qoldimi?» degan savol tug'ilsa, skladchi ro'yxatga
               qaytishi kerak. Butunlay tashlab yuborish esa endi
               yig'ib olish ekranidan qilinadi. */
            onBack={store.back}
            pending={issue.isPending}
            blocker={stepError}
          error={
            issue.isError ? requisitionErrorText(issue.error, t('handover.submitFailed')) : null
          }
        />
        </>
      ) : null}
    </div>
  );
};
